// Sponsor banner builder — makes a 1300×200 banner for the Style Editor.
//
// Two ways in:
//   * "Text & photos"  — a few lines about the sponsor plus up to four
//                        photos and a logo.
//   * "Finished ad"    — one finished ad (a print ad, a flyer, a square
//                        social ad). The AI reads it and re-lays it out
//                        as a wide strip.
//
// The AI only decides the wording, colours and layout (a small "spec");
// the server draws the banner from that spec with the real photo and
// logo. That is why the second step can be edited freely — changing a
// word or a colour just redraws, with no further AI call.
//
// The art-direction prompt is editable in Settings → Sponsor ads ·
// banner prompt and travels with the prompts export / import file.
import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  X, Loader2, Sparkles, Upload, Trash2, RotateCcw, Plus, Image as ImageIcon,
} from "lucide-react";
import { api } from "@/lib/api";

const MAX_PHOTOS = 4;
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif";
const LIMITS = { headline: 60, subline: 90, cta: 28, contact: 70 };

const LAYOUTS = [
  ["photo_left", "Photo on the left"],
  ["photo_right", "Photo on the right"],
  ["photo_full", "Photo behind the text"],
  ["text_only", "No photo"],
];
// Mirrors FONT_CHOICES in backend/ad_banner.py.
const FONTS = [
  ["montserrat", "Montserrat"], ["poppins", "Poppins"], ["roboto", "Roboto"],
  ["open-sans", "Open Sans"], ["lato", "Lato"], ["oswald", "Oswald (condensed)"],
  ["merriweather", "Merriweather (serif)"], ["lora", "Lora (serif)"],
  ["bebas-neue", "Bebas Neue (display)"], ["sans", "Sans (system)"],
  ["serif", "Serif (system)"],
];

const LABEL = "text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display";
const INPUT =
  "w-full text-sm border border-[var(--border)] px-2 py-1.5 bg-white outline-none focus:border-[var(--ink)]";
const BTN =
  "text-xs inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-40";
const BTN_PRIMARY =
  "text-xs inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate-900)] text-white hover:opacity-90 disabled:opacity-40";

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" && d ? d : e?.message || fallback;
}

function FileChips({ files, onRemove }) {
  if (!files.length) return null;
  return (
    <ul className="mt-2 flex flex-wrap gap-1.5">
      {files.map((f, i) => (
        <li
          key={`${f.name}-${f.size}-${f.lastModified}`}
          className="text-[11px] inline-flex items-center gap-1.5 border border-[var(--border)] px-2 py-1 max-w-full"
        >
          <ImageIcon className="w-3 h-3 shrink-0 text-[var(--ink-muted)]" />
          <span className="truncate max-w-[180px]">{f.name}</span>
          <button
            type="button"
            onClick={() => onRemove(i)}
            title="Remove"
            className="text-[var(--ink-muted)] hover:text-red-600"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function AdBannerDialog({ ad, onClose, onInsert }) {
  // step: "compose" (inputs) → "review" (preview + fine-tuning)
  const [step, setStep] = useState("compose");
  const [source, setSource] = useState("parts");      // "parts" | "finished"
  const [brief, setBrief] = useState("");
  const [photos, setPhotos] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [useClientLogo, setUseClientLogo] = useState(!!ad?.logo_path);
  const [finishedAd, setFinishedAd] = useState(null);
  const [spec, setSpec] = useState(null);
  const [sources, setSources] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [drawFailed, setDrawFailed] = useState(false);
  const photoInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const adInputRef = useRef(null);
  const urlRef = useRef(null);
  const seqRef = useRef(0);
  const abortRef = useRef(null);
  const backdropDownRef = useRef(false);

  const generate = useMutation({
    mutationFn: () =>
      api.generateAdBanner({
        brief,
        adId: ad?.id || "",
        useClientLogo: useClientLogo && !logoFile,
        photos,
        logo: logoFile,
        finishedAd: source === "finished" ? finishedAd : null,
      }),
    onSuccess: (r) => {
      setSpec(r.spec);
      setSources(r.sources);
      setStep("review");
    },
    onError: (e) => {
      // Prompt-only mode already showed its own toast (lib/api.js).
      if (e?.isPromptOnly) return;
      toast.error(errText(e, "Could not create the banner"));
    },
  });

  const insert = useMutation({
    mutationFn: () => api.saveAdBanner(spec, sources),
    onSuccess: (r) => onInsert(r.path),
    onError: (e) => toast.error(errText(e, "Could not add the banner")),
  });

  // Redraw whenever the spec changes. No AI involved — this is the
  // server drawing the same banner with the edited words / colours.
  useEffect(() => {
    if (step !== "review" || !spec || !sources) return undefined;
    const t = setTimeout(async () => {
      const seq = ++seqRef.current;
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setDrawing(true);
      try {
        const blob = await api.renderAdBanner(spec, sources, ctrl.signal);
        if (seq !== seqRef.current) return;
        const url = URL.createObjectURL(blob);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = url;
        setPreviewUrl(url);
        setDrawFailed(false);
      } catch (_e) {
        if (seq !== seqRef.current) return;
        setDrawFailed(true);
      } finally {
        if (seq === seqRef.current) setDrawing(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [step, spec, sources]);

  useEffect(() => () => {
    abortRef.current?.abort();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const addPhotos = (list) => {
    const incoming = Array.from(list || []).filter((f) => f.type?.startsWith("image/"));
    if (!incoming.length) return;
    const merged = [...photos, ...incoming];
    if (merged.length > MAX_PHOTOS) {
      toast.message(`Only the first ${MAX_PHOTOS} photos are used`);
    }
    setPhotos(merged.slice(0, MAX_PHOTOS));
  };
  const patch = (p) => setSpec((s) => ({ ...s, ...p }));

  const busy = generate.isPending || insert.isPending;
  const canCreate =
    !generate.isPending &&
    (source === "finished"
      ? !!finishedAd
      : !!(brief.trim() || photos.length || ad?.id));
  const hasAdPhoto = !!(sources?.finished_ad && spec?.ad_photo_box);
  const hasAdLogo = !!(sources?.finished_ad && spec?.ad_logo_box);
  const hasAnyPhoto = !!(sources?.photos?.length || (hasAdPhoto && spec?.use_ad_photo));
  const hasAnyLogo = !!(sources?.logo || hasAdLogo);

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onMouseDown={(e) => { backdropDownRef.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        if (e.target === e.currentTarget && backdropDownRef.current && !busy) onClose();
      }}
    >
      <div
        data-testid="ad-banner-dialog"
        className="w-full max-w-4xl max-h-full bg-white border border-[var(--border)] shadow-xl flex flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div>
            <div className="overline">Style editor · {ad?.name}</div>
            <h2 className="font-serif-display text-lg">Create a banner · 1300 × 200</h2>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            data-testid="ad-banner-close"
            className="text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === "compose" && (
          <div className="p-5 space-y-4 overflow-y-auto">
            <div className="flex gap-1">
              {[["parts", "From text & photos"], ["finished", "From a finished ad"]].map(([k, label]) => (
                <button
                  key={k}
                  data-testid={`ad-banner-source-${k}`}
                  onClick={() => setSource(k)}
                  className={
                    "text-xs px-3 py-1.5 border " +
                    (source === k
                      ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                      : "border-[var(--border)] hover:border-[var(--ink)]")
                  }
                >
                  {label}
                </button>
              ))}
            </div>

            {source === "finished" && (
              <div>
                <div className={LABEL}>The finished ad</div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="ad-banner-finished-upload"
                    onClick={() => adInputRef.current?.click()}
                    className={BTN}
                  >
                    <Upload className="w-3 h-3" />
                    {finishedAd ? "Replace" : "Choose image"}
                  </button>
                  <span className="text-[11px] text-[var(--ink-muted)]">
                    PNG, JPG or WebP — a print ad, flyer or social ad. For a PDF, export or
                    screenshot it as an image first.
                  </span>
                </div>
                <input
                  ref={adInputRef}
                  type="file"
                  accept={ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setFinishedAd(f);
                    e.target.value = "";
                  }}
                />
                <FileChips
                  files={finishedAd ? [finishedAd] : []}
                  onRemove={() => setFinishedAd(null)}
                />
                <p className="text-[11px] text-[var(--ink-muted)] mt-2">
                  The wording, colours, main picture and logo are taken from the ad and
                  re-arranged into a wide strip. Add photos below if you'd rather it used
                  those instead of the ad's own picture.
                </p>
              </div>
            )}

            <div>
              <div className={LABEL}>
                {source === "finished" ? "Anything to add or change (optional)" : "What should it say?"}
              </div>
              <textarea
                data-testid="ad-banner-brief"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={source === "finished" ? 3 : 5}
                maxLength={4000}
                placeholder={
                  source === "finished"
                    ? "e.g. Use the phone number, not the address. Make the button say “Reserve now”."
                    : "Paste whatever the sponsor sent: the business, what they offer, any promotion, phone, website…"
                }
                className={INPUT + " resize-y"}
              />
              {ad?.id && (
                <p className="text-[11px] text-[var(--ink-muted)] mt-1">
                  This sponsor's saved title, subhead, call to action, message for post,
                  phone and website are sent along automatically.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className={LABEL}>Photos (up to {MAX_PHOTOS})</div>
                <button
                  data-testid="ad-banner-photos-upload"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={photos.length >= MAX_PHOTOS}
                  className={BTN}
                >
                  <Plus className="w-3 h-3" /> Add photos
                </button>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept={ACCEPT}
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    addPhotos(e.target.files);
                    e.target.value = "";
                  }}
                />
                <FileChips
                  files={photos}
                  onRemove={(i) => setPhotos(photos.filter((_, n) => n !== i))}
                />
                <p className="text-[11px] text-[var(--ink-muted)] mt-1">
                  One photo is used on the banner; with several, the best fit is chosen and
                  you can switch afterwards.
                </p>
              </div>
              <div>
                <div className={LABEL}>Logo</div>
                {ad?.logo_path && (
                  <label className="flex items-center gap-2 text-xs mb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="ad-banner-use-client-logo"
                      checked={useClientLogo && !logoFile}
                      disabled={!!logoFile}
                      onChange={(e) => setUseClientLogo(e.target.checked)}
                    />
                    Use {ad.name}'s saved logo
                  </label>
                )}
                <button
                  data-testid="ad-banner-logo-upload"
                  onClick={() => logoInputRef.current?.click()}
                  className={BTN}
                >
                  <Upload className="w-3 h-3" />
                  {logoFile ? "Replace logo" : "Upload a different logo"}
                </button>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept={ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setLogoFile(f);
                    e.target.value = "";
                  }}
                />
                <FileChips
                  files={logoFile ? [logoFile] : []}
                  onRemove={() => setLogoFile(null)}
                />
              </div>
            </div>
          </div>
        )}

        {step === "review" && spec && (
          <div className="p-5 space-y-4 overflow-y-auto">
            <div
              className={
                "relative w-full border bg-neutral-100 " +
                (drawFailed ? "border-red-500" : "border-[var(--border)]")
              }
              style={{ aspectRatio: "1300 / 200" }}
            >
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="Banner preview"
                  data-testid="ad-banner-preview"
                  className="absolute inset-0 w-full h-full"
                />
              )}
              {drawing && (
                <div className="absolute top-1 right-1 bg-white/90 border border-[var(--border)] px-2 py-0.5 text-[10px] font-mono-display flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Drawing…
                </div>
              )}
              {drawFailed && !drawing && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-red-600">
                  Could not draw the banner — change anything to retry.
                </div>
              )}
            </div>
            <p className="text-[11px] text-[var(--ink-muted)]">
              Edit anything below — the banner redraws straight away, with no new AI call.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["headline", "Headline"], ["subline", "Supporting line"],
                ["cta", "Button text"], ["contact", "Contact line"],
              ].map(([k, label]) => (
                <label key={k} className="block">
                  <div className={LABEL}>
                    {label}{" "}
                    <span className="normal-case tracking-normal">
                      ({(spec[k] || "").length}/{LIMITS[k]})
                    </span>
                  </div>
                  <input
                    data-testid={`ad-banner-field-${k}`}
                    value={spec[k] || ""}
                    maxLength={LIMITS[k]}
                    onChange={(e) => patch({ [k]: e.target.value })}
                    placeholder={k === "cta" ? "Leave empty for no button" : ""}
                    className={INPUT}
                  />
                </label>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <label className="block">
                <div className={LABEL}>Layout</div>
                <select
                  data-testid="ad-banner-layout"
                  value={hasAnyPhoto ? spec.layout : "text_only"}
                  onChange={(e) => patch({ layout: e.target.value })}
                  className="w-full text-xs border border-[var(--border)] px-2 py-1.5 bg-white"
                >
                  {LAYOUTS.map(([k, label]) => (
                    <option key={k} value={k} disabled={!hasAnyPhoto && k !== "text_only"}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <div className={LABEL}>Font</div>
                <select
                  data-testid="ad-banner-font"
                  value={spec.font_family}
                  onChange={(e) => patch({ font_family: e.target.value })}
                  className="w-full text-xs border border-[var(--border)] px-2 py-1.5 bg-white"
                >
                  {FONTS.map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
              </label>
              <div>
                <div className={LABEL}>Background · Button</div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    data-testid="ad-banner-bg"
                    value={spec.background_color}
                    onChange={(e) => patch({ background_color: e.target.value })}
                    className="h-8 w-10 border border-[var(--border)] bg-transparent cursor-pointer"
                  />
                  <input
                    type="color"
                    data-testid="ad-banner-accent"
                    value={spec.accent_color}
                    onChange={(e) => patch({ accent_color: e.target.value })}
                    className="h-8 w-10 border border-[var(--border)] bg-transparent cursor-pointer"
                  />
                </div>
              </div>
              <div>
                <div className={LABEL}>Text colour</div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    data-testid="ad-banner-text-color"
                    value={spec.text_color && spec.text_color !== "auto" ? spec.text_color : "#ffffff"}
                    onChange={(e) => patch({ text_color: e.target.value })}
                    className="h-8 w-10 border border-[var(--border)] bg-transparent cursor-pointer"
                  />
                  <button
                    data-testid="ad-banner-text-auto"
                    onClick={() => patch({ text_color: "auto" })}
                    className={
                      "text-[10px] px-2 py-1 border " +
                      (spec.text_color === "auto"
                        ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                        : "border-[var(--border)] hover:border-[var(--ink)]")
                    }
                  >
                    Auto
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
              {(sources?.photos?.length || 0) > 1 && (
                <label className="flex items-center gap-2">
                  Photo
                  <select
                    data-testid="ad-banner-photo-index"
                    value={Math.min(spec.photo_index || 0, sources.photos.length - 1)}
                    onChange={(e) => patch({ photo_index: Number(e.target.value) })}
                    className="text-xs border border-[var(--border)] px-2 py-1 bg-white"
                  >
                    {sources.photos.map((p, i) => (
                      <option key={p} value={i}>#{i + 1}</option>
                    ))}
                  </select>
                </label>
              )}
              {spec.layout === "photo_full" && hasAnyPhoto && (
                <label className="flex items-center gap-2">
                  Darken photo
                  <input
                    type="range"
                    min="0"
                    max="90"
                    step="5"
                    data-testid="ad-banner-dim"
                    value={spec.photo_dim}
                    onChange={(e) => patch({ photo_dim: Number(e.target.value) })}
                    className="accent-[var(--accent)]"
                  />
                  <span className="font-mono-display text-[10px]">{spec.photo_dim}%</span>
                </label>
              )}
              {hasAnyLogo && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="ad-banner-show-logo"
                    checked={spec.show_logo !== false}
                    onChange={(e) => patch({ show_logo: e.target.checked })}
                  />
                  Show logo
                </label>
              )}
              {hasAdPhoto && !sources?.photos?.length && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="ad-banner-use-ad-photo"
                    checked={spec.use_ad_photo !== false}
                    onChange={(e) => patch({ use_ad_photo: e.target.checked })}
                  />
                  Use the picture cut from the ad
                </label>
              )}
              {hasAdLogo && !sources?.logo && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="ad-banner-use-ad-logo"
                    checked={spec.use_ad_logo !== false}
                    onChange={(e) => patch({ use_ad_logo: e.target.checked })}
                  />
                  Use the logo cut from the ad
                </label>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 px-5 py-3 border-t border-[var(--border)]">
          {step === "review" ? (
            <>
              <button
                onClick={() => setStep("compose")}
                disabled={busy}
                data-testid="ad-banner-back"
                className={BTN}
              >
                Back to the inputs
              </button>
              <button
                onClick={() => generate.mutate()}
                disabled={busy}
                data-testid="ad-banner-regenerate"
                title="Ask the AI for a different take from the same inputs"
                className={BTN}
              >
                {generate.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RotateCcw className="w-3 h-3" />
                )}
                Try another version
              </button>
              <button
                onClick={() => insert.mutate()}
                disabled={busy || drawing || !previewUrl}
                data-testid="ad-banner-insert"
                className={BTN_PRIMARY + " ml-auto"}
              >
                {insert.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Plus className="w-3 h-3" />
                )}
                Add to the artboard
              </button>
            </>
          ) : (
            <>
              <span className="text-[11px] text-[var(--ink-muted)]">
                Takes about 10–20 seconds. You can fine-tune the result before adding it.
              </span>
              <button
                onClick={() => generate.mutate()}
                disabled={!canCreate}
                data-testid="ad-banner-create"
                className={BTN_PRIMARY + " ml-auto"}
              >
                {generate.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Sparkles className="w-3 h-3" />
                )}
                {generate.isPending ? "Designing…" : "Create banner"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
